/**
 * Pre-compiled regex patterns for token reference detection.
 *
 * All patterns are compiled once at module load time.
 * Every consumer imports the same singleton instances -- zero per-call allocation.
 *
 * @module @smoothie-framework/ui-core/resolve
 */
/** Matches a standalone token reference: `{colors.blue.500}` */
export const RE_STANDALONE_REF = /^\{([^{}]+)\}$/;
/**
 * Matches a single embedded token reference (non-global).
 *
 * Use this for `.test()` checks and as the source pattern for `matchAll`.
 * For global replacement, pass this to `String.prototype.replaceAll` or
 * use `createEmbeddedRefsGlobal()` which returns a fresh global instance.
 */
export const RE_EMBEDDED_REF = /\{([^{}]+)\}/;
/**
 * Create a fresh global regex for embedded token references.
 *
 * Returns a new RegExp instance each time — no shared `lastIndex` state.
 * Use this instead of a module-level `/g` regex to avoid cross-call
 * contamination (safe for concurrent / re-entrant calls).
 */
export function createEmbeddedRefsGlobal() {
    return /\{([^{}]+)\}/g;
}
/**
 * Matches a categorized token reference: `{category.path}`.
 *
 * Captures:
 *   [1] category — word characters plus hyphens (e.g. `colors`, `spacing`, `code-block`)
 *   [2] path     — everything after the first dot (e.g. `blue.500`)
 *
 * Used by theme validation to decompose references into category + path.
 */
export const RE_CATEGORIZED_REF = /^\{([a-zA-Z][\w-]*)\.(.+)\}$/;
/**
 * Matches any standalone `{…}` reference (less restrictive than
 * `RE_CATEGORIZED_REF` — accepts any non-empty content between braces).
 */
export const RE_ANY_REF = /^\{.+\}$/;
/**
 * Fast check: does the string look like a standalone token reference?
 *
 * Uses a pre-compiled regex -- no allocation per call.
 */
export function isTokenRef(value) {
    return RE_STANDALONE_REF.test(value);
}
/**
 * Fast pre-check: could the string contain any embedded token references?
 *
 * Uses indexOf internally -- cheaper than a regex test for the common
 * case where strings contain no references at all.
 */
export function hasEmbeddedRefs(value) {
    return value.includes('{');
}
export function parseTokenReferenceContent(content) {
    const match = /^([a-zA-Z][\w-]*)\.(.+?)(?:\/(\d{1,3}))?$/.exec(content.trim());
    if (!match) {
        return undefined;
    }
    const [, category, path, opacitySource] = match;
    if (!category || !path) {
        return undefined;
    }
    if (opacitySource === undefined) {
        return { category, path };
    }
    const opacity = Number(opacitySource);
    if (!Number.isInteger(opacity) || opacity < 0 || opacity > 100) {
        return undefined;
    }
    return {
        category,
        path,
        opacity,
    };
}
export function parseStandaloneTokenReference(reference) {
    const match = RE_STANDALONE_REF.exec(reference);
    if (!match?.[1]) {
        return undefined;
    }
    return parseTokenReferenceContent(match[1]);
}
//# sourceMappingURL=patterns.js.map